import { z } from "zod";

const equipmentStatusSchema = z.object({
  id: z.number(),
  name: z.string(),
});

const linehaulInfoSchema = z
  .object({
    originSicId: z.number().nullable(),
    destinationSicId: z.number().nullable(),
  })
  .nullable();

const assignmentSchema = z
  .object({
    id: z.number(),
    type: z.enum(["Route", "Trap", "Trip", "Lane"]),
    name: z.string(),
    status: z.string(),
    linehaulInfo: linehaulInfoSchema,
  })
  .nullable();

const loadSummarySchema = z.object({
  bills: z.number(),
  weight: z.number(),
  pallets: z.number(),
  pieces: z.number(),
  handlingUnits: z.number().default(0),
});

const ownershipSchema = z.object({
  ownershipType: z.string(),
  ownershipScacCode: z.string().nullable(),
});

const equipmentTypeSchema = z.object({
  id: z.number(),
  name: z.enum(["Trailer", "Tractor", "Straight Truck", "Forklift"]),
});

const equipmentSchema = z.object({
  equipmentId: z.number(),
  refNumber: z.string(),
  doorNumber: z.union([z.number(), z.string()]).nullable(),
  status: equipmentStatusSchema.nullable(),
  assignedTo: assignmentSchema,
  homeSicId: z.number(),
  loadSummary: loadSummarySchema,
  equipmentType: equipmentTypeSchema,
  lengthFt: z.number().nullable(),
  ownership: ownershipSchema,
  currentSealNumber: z.string().nullable(),
  lastUpdateDate: z.coerce.string(),
  daysAging: z.number(),
});

export const equipmentResponseSchema = z.object({
  available: z.array(equipmentSchema),
  assigned: z.array(equipmentSchema),
  loadAndHold: z.array(equipmentSchema),
});

export type Assignment = z.infer<typeof assignmentSchema>;
export type LoadSummary = z.infer<typeof loadSummarySchema>;
export type Equipment = z.infer<typeof equipmentSchema>;
export type EquipmentResponse = z.infer<typeof equipmentResponseSchema>;

export interface EquipmentOptionDisplay {
  id: number;
  refNumber: string;
  statusName: string;
  locationDisplay: string;
  contentDisplay: string | null;
  isAvailable: boolean;
  bucket: EquipmentBucket;
  rawData: Equipment;
}

export type EquipmentBucket = "available" | "assigned" | "loadAndHold";

export type EquipmentTypeName = "Trailer" | "Tractor" | "Straight Truck";

const equipmentGroupLabels = {
  Trailer: {
    available: "Available Trailers",
    unavailable: "Assigned Trailers",
    loadAndHold: "Load & Hold Trailers",
  },
  Tractor: {
    available: "Available Tractors",
    unavailable: "Assigned Tractors",
    loadAndHold: "Load & Hold Tractors",
  },
  "Straight Truck": {
    available: "Available STs",
    unavailable: "Assigned STs",
    loadAndHold: "Load & Hold STs",
  },
} as const satisfies Record<
  EquipmentTypeName,
  { available: string; unavailable: string; loadAndHold: string }
>;

const defaultGroupLabels = {
  available: "Available to reposition",
  unavailable: "Unavailable to reposition",
  loadAndHold: "Load & Hold",
} as const;

export const getEquipmentGroupLabels = (
  equipmentTypeName?: EquipmentTypeName
) =>
  equipmentTypeName
    ? equipmentGroupLabels[equipmentTypeName]
    : defaultGroupLabels;

// Component props
export interface EquipmentDropdownProps {
  value: EquipmentOptionDisplay | null;
  onChange: (value: EquipmentOptionDisplay | null) => void;
  options: EquipmentOptionDisplay[];
  label?: string;
  disabled?: boolean;
  getOptionDisabled?: (option: EquipmentOptionDisplay) => boolean;
  disableClearable?: boolean;
  error?: boolean;
  helperText?: string;
  equipmentTypeName?: EquipmentTypeName;

  inputValue?: string;
  onInputChange?: (value: string) => void;
  loading?: boolean;
  showSicCode?: boolean;
}
